import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import styled from 'styled-components';

import BoundImageModal from '../../components/boundImageModal';
import {Link, StyledPageCenterContainer, pxToEm} from '../../components/globalStyles';
import HexagonIconImage, {HexagonBaseBg} from '../../components/hexagonIconImage';
import HexagonLeaflet, {Direction as HexagonLeafletDirection} from '../../components/hexagonLeaflet';
import Layout from '../../components/layout/layout';
import PageViewHeadline from '../../components/pageViewHeadline';
import SeeItInActionButton from '../../components/seeItInActionButton';
import {Product, ProductToAppRoutePath, ProductToHexagonIconSrc, ProductToText} from '../../data/product';
import HybridMultiCloudSvg from '../../media/hexagonIconImage/icons/hybrid_multi_cloud.svg';
import AnalyticsWorkflowSvg from '../../media/onPremiseAnalyticsPageView/analytics_workflow.svg';

import OnPremiseAnalyticsAccordions from './onPremiseAnalyticsAccordions';

//#region Styled Components

const StyledLeafletH2 = styled.h2`
  font-size: ${pxToEm(22)}em;
  color: ${baseVariables.colors.midnight};
`;

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledTwoColumnsSection = styled.div`
  padding-top: ${pxToEm(124)}em;
  padding-bottom: ${pxToEm(114)}em;

  display: grid;
  justify-content: center;
  grid-template-columns: 50% 50%;

  > ${StyledContentColumn} {
    :nth-child(1) {
      border-right: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-right: 5%;
    }

    :nth-child(2) {
      padding-left: 5%;
    }
  }
`;

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  margin: 0 0 ${pxToEm(55) / pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
`;

const StyledH3 = styled.h3`
  font-size: ${pxToEm(22)}em;
  color: ${baseVariables.colors.midnight};
  margin: ${pxToEm(23) / pxToEm(22)}em 0 ${pxToEm(10) / pxToEm(22)}em;
`;

const StyledP = styled.p`
  font-size: ${pxToEm(20)}em;
  line-height: 1.6;
  margin: 0 0 ${pxToEm(39) / pxToEm(20)}em;
`;

const StyledImg = styled.img`
  width: 100%;
`;

//#endregion

const OnPremiseAnalyticsPageView: React.FunctionComponent = () => {
  return (
    <Layout>
      <PageViewHeadline
        topSubHeadline={ProductToText[Product.OnPremiseAnalytics]}
        headline={'Control and present your data exactly as you need to, every time'}
        badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.OnPremiseAnalytics]} />}
        leftBadgeComponent={
          <SeeItInActionButton
            external={true}
            to="http://bitmovin.com/dashboard/analytics/data?orgId=6af25605-1bd1-458d-8de9-1189f9ccf6df&3rdParty=false&apiKey=5cd462f7-73c1-424e-82d0-1a9bfabcf310" // tslint:disable-line:max-line-length
          />
        }
        rightBadgeComponent={
          <Link to={ProductToAppRoutePath[Product.CloudAnalytics]}>
            <HexagonLeaflet
              baseBg={HexagonBaseBg.Blue}
              direction={HexagonLeafletDirection.Left}
              hexagonIconSrc={HybridMultiCloudSvg}
              hexagonOverflowPx={28}>
              <StyledLeafletH2>{ProductToText[Product.CloudAnalytics]}</StyledLeafletH2>
            </HexagonLeaflet>
          </Link>
        }
      />

      <StyledPageCenterContainer>
        <StyledTwoColumnsSection>
          <StyledContentColumn>
            <StyledH2>
              Sidestep privacy and data security concerns by taking control of your analytics data storage in your own
              data center
            </StyledH2>
            <StyledH3>Take Control</StyledH3>
            <StyledP>
              Built on industry standard platforms, Docker and Kubernetes, Bitmovin Analytics enables the first true
              on-premise analytics solution in the video industry. The ability to deploy your storage cluster to your
              own data center gives you complete control over security and privacy.
            </StyledP>
            <StyledH3>Unified API</StyledH3>
            <StyledP>
              The on-premise solution runs on the same API as the cloud based system so you will have access to the same
              rich set of features and completely customizable dashboard. Contact our sales team to find out more about
              realtime, analytics in your own data center.
            </StyledP>
            <StyledH3>Technical Specifications</StyledH3>
            <OnPremiseAnalyticsAccordions />
          </StyledContentColumn>

          <StyledContentColumn>
            <BoundImageModal
              boundComponent={<StyledImg src={AnalyticsWorkflowSvg} alt="Analytics workflow" />}
              source={AnalyticsWorkflowSvg}
            />
          </StyledContentColumn>
        </StyledTwoColumnsSection>
      </StyledPageCenterContainer>
    </Layout>
  );
};

export default OnPremiseAnalyticsPageView;
